import { LangLink } from "@/components/LangLink";
import { useLanguage } from "@/lib/language";
import type { InsightMeta } from "@/components/insights/InsightShell";
import { H2, P } from "@/components/insights/prose";

export const isoRecognizedIraqMeta: InsightMeta = {
  slug: "is-iso-certification-recognized-in-iraq",
  metaTitle: {
    en: "Is ISO Certification Recognized in Iraq? | IraqCert",
    ar: "هل شهادة الأيزو معتمدة في العراق؟ | عراق سيرت",
  },
  title: {
    en: "Is ISO Certification Recognized in Iraq?",
    ar: "هل شهادة الأيزو معتمدة في العراق؟",
  },
  description: {
    en: "Check whether an ISO certificate meets a buyer’s requirements in Iraq: standard, activities, sites, issuing body, accreditation and current status.",
    ar: "تحقّق من مطابقة شهادة الأيزو لمتطلبات المشتري في العراق: المواصفة والأنشطة والمواقع والجهة المانحة والاعتماد والحالة الحالية.",
  },
  datePublished: "2026-07-21",
  dateModified: "2026-10-09",
  dateLabel: { en: "9 October 2026", ar: "9 تشرين الأول 2026" },
  faq: [
    {
      q: {
        en: "Is ISO certification recognized in Iraq?",
        ar: "هل شهادة الأيزو معتمدة في العراق؟",
      },
      a: {
        en: "Acceptance depends on the buyer or authority and the specific tender or registration requirement. Check the requested standard, activities, sites, issuer, accreditation conditions and validity. Certification alone does not secure approval or a contract.",
        ar: "يتوقف القبول على المشتري أو الجهة المعنية ومتطلبات المناقصة أو التسجيل المحددة. راجع المواصفة والأنشطة والمواقع والجهة المانحة وشروط الاعتماد والصلاحية. ولا تضمن الشهادة وحدها القبول أو الحصول على عقد.",
      },
    },
    {
      q: {
        en: "How should we check the accreditation behind a certificate?",
        ar: "كيف نتحقق من الاعتماد الذي تستند إليه الشهادة؟",
      },
      a: {
        en: "Identify the certification body and check its current entry and scope in the accreditation body’s directory. If the buyer names a recognition arrangement or approved issuer, verify that specific condition rather than relying on a country label.",
        ar: "حدّد جهة منح الشهادة وراجع سجلها الحالي ونطاقها في دليل هيئة الاعتماد. وإذا حدّد المشتري اتفاقية اعتراف أو جهة مانحة مقبولة، فتحقق من هذا الشرط تحديداً بدلاً من الاعتماد على اسم البلد.",
      },
    },
    {
      q: {
        en: "Will an Iraqi ISO certificate be accepted abroad?",
        ar: "هل تُقبل شهادة الأيزو العراقية في الخارج؟",
      },
      a: {
        en: "Ask the foreign buyer to confirm the standard, scope, sites, issuer and accreditation conditions. A CertSearch record supports verification of certification; it does not establish that the certificate meets every buyer’s procurement requirements.",
        ar: "اطلب من المشتري الأجنبي تأكيد المواصفة والنطاق والمواقع والجهة المانحة وشروط الاعتماد. يساعد سجل CertSearch في التحقق من الشهادة، لكنه لا يثبت استيفاءها متطلبات الشراء لدى كل مشترٍ.",
      },
    },
    {
      q: {
        en: "Do government tenders in Iraq require ISO?",
        ar: "هل تشترط المناقصات الحكومية في العراق الأيزو؟",
      },
      a: {
        en: "Read the current tender documents. Do not assume every government tender requires ISO certification or the same set of standards. Ask the issuing authority to clarify ambiguous wording before arranging certification.",
        ar: "اقرأ وثائق المناقصة الحالية. لا تفترض أن جميع المناقصات الحكومية تشترط الأيزو أو المجموعة نفسها من المواصفات. اطلب من الجهة المصدرة توضيح أي صياغة مبهمة قبل ترتيب إجراءات الشهادة.",
      },
    },
  ],
};

export function IsoRecognizedIraqBody() {
  const { t } = useLanguage();
  return (
    <>
      <P>
        {t({
          en: "An ISO certificate can form part of a supplier’s evidence in Iraq, but acceptance is a decision against the buyer’s stated requirements. Accreditation and database verification do not replace that review. Start with ",
          ar: "يمكن أن تكون شهادة الأيزو جزءاً من أدلة المورّد في العراق، لكن قبولها يتحدد وفق متطلبات المشتري المعلنة. ولا يغني الاعتماد أو التحقق في قاعدة بيانات عن هذه المراجعة. ابدأ بـ ",
        })}
        <LangLink
          to="/insights/accredited-vs-non-accredited-iso-certificate"
          className="font-semibold text-primary hover:underline"
        >
          {t({
            en: "the difference between accredited and non-accredited certificates",
            ar: "الفرق بين الشهادة المعتمدة وغير المعتمدة",
          })}
        </LangLink>
        {t({ en: ".", ar: "." })}
      </P>

      <H2>
        {t({
          en: "Check the certificate and the issuer separately",
          ar: "تحقّق من الشهادة والجهة المانحة كلٍّ على حدة",
        })}
      </H2>
      <P>
        {t({
          en: "Match the certificate’s legal entity, standard, activities, sites and current status to the requested work. Separately confirm that the issuing body’s accreditation covers the relevant certification. If a CertSearch record is missing or inconsistent, follow up with the certification and accreditation bodies; do not infer acceptance or rejection from the search alone.",
          ar: "طابق الاسم القانوني والمواصفة والأنشطة والمواقع وحالة الشهادة الحالية مع العمل المطلوب. وتحقق بصورة مستقلة من أن اعتماد الجهة المانحة يشمل الشهادة المعنية. إذا لم يظهر سجل CertSearch أو كانت بياناته متعارضة، فاستكمل التحقق مع جهتي المنح والاعتماد؛ ولا تستنتج القبول أو الرفض من البحث وحده.",
        })}
      </P>

      <H2>
        {t({
          en: "What certificate do Iraqi tenders accept?",
          ar: "أي شهادة تقبلها المناقصات العراقية؟",
        })}
      </H2>
      <P>
        {t({
          en: "Keep the buyer’s exact requirement and your proposed evidence together. Check any named certification body, accreditation condition, scope and validity date before committing. IraqCert supports preparation and coordination; the certification body makes the certification decision and the buyer decides supplier acceptance.",
          ar: "احتفظ بنص متطلب المشتري والأدلة المقترحة معاً. راجع أي جهة مانحة محددة بالاسم وشروط الاعتماد والنطاق وتاريخ الصلاحية قبل الالتزام. تدعم عراق سيرت التحضير والتنسيق؛ وتتخذ الجهة المانحة قرار الشهادة، بينما يقرر المشتري قبول المورّد.",
        })}
      </P>

      <H2>
        {t({
          en: "What this means for exporters and vendors",
          ar: "ماذا يعني ذلك للمصدّرين والموردين",
        })}
      </H2>
      <P>
        {t({
          en: "A certificate accepted for one contract may not satisfy another. For example, DNO’s public supply-chain page identifies MNR registration for suppliers in the Kurdistan Region and says tender terms are request-specific. That is a separate procurement route, not an ISO acceptance guarantee. See our support for ",
          ar: "قد لا تفي شهادة مقبولة في عقد بمتطلبات عقد آخر. فعلى سبيل المثال، تذكر صفحة سلسلة التوريد لدى DNO تسجيل MNR للموردين في إقليم كردستان، وتوضح أن شروط المناقصة تخص كل طلب. وهذا مسار شراء مستقل وليس ضماناً لقبول شهادة الأيزو. تعرّف على دعمنا في ",
        })}
        <LangLink
          to="/services/oil-gas-registration"
          className="font-semibold text-primary hover:underline"
        >
          {t({ en: "oil and gas vendor registration", ar: "تسجيل موردي النفط والغاز" })}
        </LangLink>
        {t({ en: ".", ar: "." })}
      </P>

      <H2>{t({ en: "Sources", ar: "المصادر" })}</H2>
      <P>
        <a
          href="https://www.iso.org/certification.html"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-primary underline underline-offset-4"
        >
          {t({ en: "ISO: certification and accreditation", ar: "ISO: منح الشهادات والاعتماد" })}
        </a>
      </P>
      <P>
        <a
          href="https://www.iafcertsearch.org/"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-primary underline underline-offset-4"
        >
          {t({
            en: "IAF CertSearch: verify certification records",
            ar: "IAF CertSearch: التحقق من سجلات الشهادات",
          })}
        </a>
      </P>
      <P>
        <a
          href="https://www.dno.no/en/operations/supply-chain/"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-primary underline underline-offset-4"
        >
          {t({
            en: "DNO: supplier registration and tender-specific terms",
            ar: "DNO: تسجيل الموردين والشروط الخاصة بكل مناقصة",
          })}
        </a>
      </P>
    </>
  );
}
